import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { theme, t } from '../theme';
import { overlayCloseProps, norm } from '../utils';
import financasApi from '../services/financasApi';
import api from '../api';

function moedaInput(raw) {
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) return '';
  return (parseInt(digits, 10) / 100).toFixed(2).replace('.', ',');
}

function parseMoeda(val) {
  if (!val) return 0;
  return parseFloat(String(val).replace(/\./g, '').replace(',', '.')) || 0;
}

export default function NovaTransacaoModal({ onClose, onSaved, transacao }) {
  const ehEdicao = !!transacao;
  const hojeISO = new Date().toISOString().split('T')[0];

  const [tipo, setTipo] = useState(transacao?.tipo || 'receita');
  const [valor, setValor] = useState(transacao ? Number(transacao.valor).toFixed(2).replace('.', ',') : '');
  const [categoriaId, setCategoriaId] = useState(transacao?.categoria_id || '');
  const [metodoId, setMetodoId] = useState(transacao?.metodo_pagamento_id || '');
  const [data, setData] = useState(transacao?.data || hojeISO);
  const [descricao, setDescricao] = useState(transacao?.descricao || '');
  const [clienteId, setClienteId] = useState(transacao?.cliente_id || '');
  const [fornecedor, setFornecedor] = useState(transacao?.fornecedor || '');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const [categorias, setCategorias] = useState([]);
  const [metodos, setMetodos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [novaCategoria, setNovaCategoria] = useState(false);
  const [nomeNovaCategoria, setNomeNovaCategoria] = useState('');
  const [criandoCategoria, setCriandoCategoria] = useState(false);

  useEffect(() => {
    Promise.all([
      financasApi.listarCategorias({ apenas_ativas: 'true' }),
      financasApi.listarMetodos({ apenas_ativas: 'true' }),
      api.get('/api/clientes'),
    ]).then(([cs, ms, cli]) => {
      setCategorias(cs.data || []);
      setMetodos(ms.data || []);
      setClientes(cli.data || []);
    }).catch(() => {});
  }, []);

  // Quando troca o tipo, reseta categoria selecionada
  useEffect(() => {
    if (categoriaId) {
      const c = categorias.find(x => x.id === Number(categoriaId));
      if (c && c.tipo !== tipo) setCategoriaId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);

  const categoriasFiltradas = categorias.filter(c => c.tipo === tipo && c.ativo);

  async function criarCategoriaInline() {
    if (!nomeNovaCategoria.trim()) return;
    setCriandoCategoria(true);
    try {
      const res = await financasApi.criarCategoria({ nome: nomeNovaCategoria.trim(), tipo });
      setCategorias(prev => [...prev, res.data]);
      setCategoriaId(res.data.id);
      setNovaCategoria(false);
      setNomeNovaCategoria('');
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao criar categoria');
    } finally { setCriandoCategoria(false); }
  }

  async function salvar(e) {
    e.preventDefault();
    setErro('');
    const v = parseMoeda(valor);
    if (!v || v <= 0) { setErro('Informe um valor válido'); return; }
    if (!categoriaId) { setErro('Escolha uma categoria'); return; }
    if (!metodoId) { setErro('Escolha um método de pagamento'); return; }

    setSalvando(true);
    const payload = {
      tipo,
      valor: v,
      categoria_id: Number(categoriaId),
      metodo_pagamento_id: Number(metodoId),
      data: data || hojeISO,
      descricao: descricao || null,
      cliente_id: tipo === 'receita' && clienteId ? Number(clienteId) : null,
      fornecedor: tipo === 'despesa' ? (fornecedor || null) : null,
    };
    try {
      if (ehEdicao) await financasApi.atualizarTransacao(transacao.id, payload);
      else await financasApi.criarTransacao(payload);
      onSaved && onSaved();
    } catch (e2) {
      setErro(e2.response?.data?.erro || 'Erro ao salvar');
    } finally { setSalvando(false); }
  }

  const corPrim = tipo === 'receita' ? '#16a34a' : '#dc2626';

  return (
    <div style={t.overlay} {...overlayCloseProps(onClose)}>
      <div style={{ ...t.modal, maxWidth: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{ehEdicao ? 'Editar Transação' : 'Nova Transação'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#bbb' }}><FaTimes /></button>
        </div>

        {erro && <div style={t.error}>{erro}</div>}

        <form onSubmit={salvar} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Tipo: receita/despesa */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              { v: 'receita', label: '💰 Receita', cor: '#16a34a' },
              { v: 'despesa', label: '💸 Despesa', cor: '#dc2626' },
            ].map(opt => (
              <button key={opt.v} type="button" onClick={() => setTipo(opt.v)}
                style={{
                  padding: '12px', borderRadius: 12, fontSize: 14, fontWeight: 700,
                  cursor: 'pointer', userSelect: 'none',
                  border: `1.5px solid ${tipo === opt.v ? opt.cor : theme.colors.border}`,
                  background: tipo === opt.v ? `${opt.cor}15` : '#fff',
                  color: tipo === opt.v ? opt.cor : theme.colors.textMuted,
                }}>
                {opt.label}
              </button>
            ))}
          </div>

          <div>
            <label style={t.label}>Valor (R$) *</label>
            <input value={valor} onChange={e => setValor(moedaInput(e.target.value))}
              placeholder="0,00" autoFocus style={t.input} />
          </div>

          <div>
            <label style={t.label}>Categoria *</label>
            {!novaCategoria ? (
              <div style={{ display: 'flex', gap: 6 }}>
                <select value={categoriaId} onChange={e => setCategoriaId(e.target.value)} style={{ ...t.select, flex: 1 }}>
                  <option value="">Selecione...</option>
                  {categoriasFiltradas.map(c => (
                    <option key={c.id} value={c.id}>{c.nome}{c.is_sistema ? '' : ' ⭐'}</option>
                  ))}
                </select>
                <button type="button" onClick={() => setNovaCategoria(true)}
                  style={{ ...t.btnSecondary, padding: '0 12px', fontSize: 12 }}>+ Nova</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 6 }}>
                <input value={nomeNovaCategoria} onChange={e => setNomeNovaCategoria(e.target.value)}
                  placeholder="Nome da nova categoria" style={{ ...t.input, flex: 1 }} autoFocus />
                <button type="button" onClick={criarCategoriaInline} disabled={criandoCategoria}
                  style={{ ...t.btnPrimary, padding: '0 12px', fontSize: 12, background: corPrim }}>
                  {criandoCategoria ? '...' : 'Criar'}
                </button>
                <button type="button" onClick={() => { setNovaCategoria(false); setNomeNovaCategoria(''); }}
                  style={{ ...t.btnSecondary, padding: '0 12px', fontSize: 12 }}>Cancelar</button>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={t.label}>Método *</label>
              <select value={metodoId} onChange={e => setMetodoId(e.target.value)} style={t.select}>
                <option value="">Selecione...</option>
                {metodos.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
              </select>
            </div>
            <div>
              <label style={t.label}>Data *</label>
              <input type="date" value={data} onChange={e => setData(e.target.value)} style={t.input} />
            </div>
          </div>

          {tipo === 'receita' ? (
            <div>
              <label style={t.label}>Cliente (opcional)</label>
              <select value={clienteId} onChange={e => setClienteId(e.target.value)} style={t.select}>
                <option value="">—</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <label style={t.label}>Fornecedor (opcional)</label>
              <input value={fornecedor} onChange={e => setFornecedor(e.target.value)}
                placeholder="Ex: Atacadão" style={t.input} />
            </div>
          )}

          <div>
            <label style={t.label}>Descrição (opcional)</label>
            <textarea value={descricao} onChange={e => setDescricao(e.target.value)} rows="2"
              style={{ ...t.input, resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button type="submit" disabled={salvando}
              style={{ ...t.btnPrimary, flex: 1, justifyContent: 'center', background: corPrim }}>
              {salvando ? 'Salvando...' : (ehEdicao ? 'Salvar' : 'Registrar')}
            </button>
            <button type="button" onClick={onClose} style={t.btnSecondary}>Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}
